import { native } from '@forest/native-services'
import { Dialog } from '@forest/shared/solid'
import {
  createEffect,
  createResource,
  createSignal,
  Match,
  on,
  Show,
  Switch,
} from 'solid-js'
import { LossScreen } from '~/components/loss-screen'
import { PlanPicker } from '~/components/plan-picker'
import { SubscribedView } from '~/components/subscribed-view'
import { trackPageViews } from '~/lib/analytics'
import { loadPaywall } from '~/lib/bootstrap'
import { createPurchase } from '~/lib/purchase'
import { createScreen } from '~/lib/screen'
import { showUnavailable } from '~/lib/unavailable'

export function App() {
  const { screen, openPlanPicker } = createScreen()
  // Who the page applies to depends on the screen native launched it at.
  const launchScreen = screen()
  const [paywall] = createResource(() => loadPaywall(launchScreen))
  const available = () => {
    const loaded = paywall()
    return loaded && !('code' in loaded) ? loaded : undefined
  }

  // The bootstrap's one outcome is a reveal: onto the page, or onto the error
  // dialog when it cannot show (GLOSSARY.md "Resubscribe paywall"). Tracks
  // the bootstrap alone: opening the dialog reads the dialog's own state.
  createEffect(
    on(paywall, (loaded) => {
      if (loaded === undefined) return
      if ('code' in loaded) showUnavailable(loaded, launchScreen)
      // Reveal once this update has rendered: a dialog opened here renders
      // only after the effect.
      queueMicrotask(() => void native.general.pageReady())
    }),
  )

  return (
    <>
      <Show when={available()}>
        {(loaded) => {
          const [isPlusRegained, setIsPlusRegained] = createSignal(false)
          const purchase = createPurchase({
            ...loaded(),
            onPlusRegained: () => setIsPlusRegained(true),
          })
          trackPageViews(screen, loaded())
          return (
            <>
              <Switch>
                <Match when={screen() === 'loss' && loaded().subscription}>
                  {(subscription) => (
                    <LossScreen
                      subscription={subscription()}
                      onRenew={openPlanPicker}
                    />
                  )}
                </Match>
                <Match when={screen() === 'plans'}>
                  <PlanPicker
                    plans={loaded().plans}
                    currentPlan={loaded().currentPlan}
                    platform={loaded().platform}
                    onPurchase={purchase}
                  />
                </Match>
              </Switch>
              {/* Only a lapsed subscriber's purchase ends on it. */}
              <Show when={!loaded().isExpiringSubscriber}>
                <SubscribedView open={isPlusRegained()} />
              </Show>
            </>
          )
        }}
      </Show>
      <Dialog />
    </>
  )
}
